import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FilterDrawer, FilterSection } from 'pages/Workshop/common';
import { Checkbox } from 'antd';
import { isEqual } from 'lodash-es';
import { GroupCheckBoxesSection } from 'pages/Workshop/common/GroupCheckBoxesSection';
import { MultiSelectSection } from 'pages/Workshop/common/MultiSelectSection';
import { LocationService } from 'services';
import { setCalendarFiltersEffect } from 'store/effects';
import {
  getAllTransactionsSelector,
  getTransactionsIsFilterSelector,
  getTransactionsPersons,
} from 'store/selectors/transactions';
import { getProjectsSelector } from 'store/selectors/projects';
import { CalendarTypeOptions } from 'settings/constants/transaction';
import { defaultCalendarTypeOptions } from 'pages/Calendar/constants';
import { getCalendarFiltersAppliedSelector } from 'store/selectors/calendar';

import styles from './styles.module.scss';

let defaultFilters = {};

export const FilterSections = () => {
  const dispatch = useDispatch();
  const locationSrv = new LocationService();
  locationSrv.setLocation(location);
  const transactionsPersons = useSelector(getTransactionsPersons);

  const clientsData = transactionsPersons.clientsArray.map((item) => ({
    value: item,
    label: item,
  }));

  const agentsOrOwnersData = transactionsPersons.agentsOrOwnersArray.map((item) => ({
    value: item,
    label: item,
  }));
  const transactionsData = useSelector(getAllTransactionsSelector);
  const projectsData = useSelector(getProjectsSelector);
  const [isFiltersAppliedLocal, setIsFiltersAppliedLocal] = useState<boolean>(false);
  const isFiltersApplied = useSelector(getTransactionsIsFilterSelector);

  const [transactionIds, setTransactionsIds] = useState<string[]>([]);
  const [projectIds, setProjectsIds] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [agentsOrOwners, setAgentsOrOwners] = useState<string[]>([]);
  const [showOverdue, setShowOverdue] = useState<boolean>(false);
  const [previousFilters, setPreviousFilters] = useState({});
  const filters = useSelector(getCalendarFiltersAppliedSelector);
  const [calendarTypeOption, setCalendarTypeOption] = useState<string[]>(
    filters?.type || defaultCalendarTypeOptions,
  );

  const getFilters = () => ({
    transactionIds: [...transactionIds, ...projectIds],
    clients,
    agentsOrOwners,
    type: calendarTypeOption,
    ...(showOverdue ? { showOverdue } : {}),
  });

  useEffect(() => {
    defaultFilters = getFilters();
    setPreviousFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (!isFiltersApplied) {
      setIsFiltersAppliedLocal(false);
      setTransactionsIds([]);
      setProjectsIds([]);
      setClients([]);
      setAgentsOrOwners([]);
    }
  }, [isFiltersApplied]);

  useEffect(() => {
    setIsFiltersAppliedLocal(
      Boolean(calendarTypeOption?.length < 9) ||
        Boolean(transactionIds?.length) ||
        Boolean(projectIds?.length) ||
        Boolean(clients?.length) ||
        Boolean(agentsOrOwners?.length) ||
        showOverdue,
    );
  }, [calendarTypeOption, transactionIds, projectIds, clients, agentsOrOwners, showOverdue]);

  const submitHandler = () => {
    dispatch(
      setCalendarFiltersEffect({
        type: calendarTypeOption,
        transactionIds: [...transactionIds, ...projectIds],
        clients,
        agentsOrOwners,
        ...(showOverdue ? { showOverdue } : {}),
      }),
    );
    setPreviousFilters(defaultFilters);
  };

  const resetHandler = () => {
    setCalendarTypeOption(defaultCalendarTypeOptions);
    setTransactionsIds([]);
    setProjectsIds([]);
    setAgentsOrOwners([]);
    setClients([]);
    setShowOverdue(false);
    dispatch(
      setCalendarFiltersEffect({
        type: defaultCalendarTypeOptions,
        clients: [],
        agentsOrOwners: [],
        transactionIds: [],
        ...(showOverdue ? { showOverdue } : {}),
      }),
    );
    setPreviousFilters(defaultFilters);
  };

  const discardHandler = () => {
    if (isEqual(previousFilters, getFilters())) {
      return;
    }

    const { transactionIds, clients, agentsOrOwners, type, showOverdue } = previousFilters as any;

    if (transactionIds) setTransactionsIds(transactionIds);
    if (clients) setClients(clients);
    if (agentsOrOwners) setAgentsOrOwners(agentsOrOwners);
    if (type) setCalendarTypeOption(type);
    if (showOverdue !== undefined) setShowOverdue(showOverdue);
  };

  return (
    <FilterDrawer
      className={styles.filterBtn}
      isFiltersApplied={isFiltersAppliedLocal}
      onDone={submitHandler}
      onReset={resetHandler}
      onDiscard={discardHandler}
      doneDisable={isEqual(previousFilters, getFilters())}
      iconOnly
    >
      <div className={styles.filterSections}>
        <FilterSection title="Address" count={transactionIds?.length}>
          <MultiSelectSection
            placeholder="Search by address ..."
            entityList={transactionsData?.map(({ address, id }) => ({
              value: id,
              label: address,
            }))}
            entity={transactionIds}
            setEntity={setTransactionsIds}
            locationPrefix
            multiSelectWrapperClassName={styles.marginZero}
          />
        </FilterSection>
        <FilterSection title="Project" count={projectIds?.length}>
          <MultiSelectSection
            placeholder="Search by project name ..."
            entityList={projectsData?.map(({ Id, Name }) => ({
              value: Id,
              label: Name,
            }))}
            entity={projectIds}
            setEntity={setProjectsIds}
            locationPrefix
            multiSelectWrapperClassName={styles.marginZero}
          />
        </FilterSection>
        <FilterSection title="Type" count={calendarTypeOption?.length}>
          <GroupCheckBoxesSection
            entityList={CalendarTypeOptions}
            entityValues={calendarTypeOption}
            setEntity={setCalendarTypeOption}
          />
          <div className={styles.separator} />
          <Checkbox
            checked={showOverdue}
            className={classNames(styles.checkbox, 'mosaikCheckbox')}
            onChange={(e: any) => setShowOverdue(e.target.checked)}
          >
            Show Overdue
          </Checkbox>
        </FilterSection>
        <FilterSection title="Client" count={clients.length}>
          <MultiSelectSection
            placeholder={'Search by client name ...'}
            entityList={clientsData}
            entity={clients}
            setEntity={setClients}
            avatarPrefix
            multiSelectWrapperClassName={styles.marginZero}
          />
        </FilterSection>

        <FilterSection title="Agent" count={agentsOrOwners.length}>
          <MultiSelectSection
            placeholder={'Search by agent name ...'}
            entityList={agentsOrOwnersData}
            entity={agentsOrOwners}
            setEntity={setAgentsOrOwners}
            avatarPrefix
            multiSelectWrapperClassName={styles.marginZero}
          />
        </FilterSection>
      </div>
    </FilterDrawer>
  );
};
